import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ChatWorkspaceSectionShell } from '../layout/index.js';
import SourceMaterialRow from './SourceMaterialRow.jsx';
import SourceDetailModal from './SourceDetailModal.jsx';

const { Text } = Typography;

function firstTruthy(...values) {
  return values.find(Boolean);
}

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

export default function ChatWorkspaceSourcesPanel({
  composerNode,
  contentMaxWidth,
  handleOpenSourcePicker,
  handleRemoveSource,
  handleRetrySource,
  handleViewSourceDetail,
  isCompactLayout,
  resolvedUILabels,
  sourceActionError,
  sourceItems,
  sourceRemovingId,
  sourceSyncLoading,
  sourceUploadLoading,
  themeTokens,
}) {
  const [pendingDeleteSourceId, setPendingDeleteSourceId] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceDropActive, setSourceDropActive] = useState(false);
  const sourceDragDepthRef = useRef(0);
  const sourceKeySet = useMemo(
    () => new Set(sourceItems.map((item) => {
      const record = Object.assign({}, item);
      return String(firstTruthy(record.source_id, record.id) ?? '').trim();
    }).filter(Boolean)),
    [sourceItems],
  );

  useEffect(() => {
    if (!pendingDeleteSourceId) {
      return;
    }
    if (sourceKeySet.has(pendingDeleteSourceId)) {
      return;
    }
    setPendingDeleteSourceId('');
  }, [pendingDeleteSourceId, sourceKeySet]);

  const handleDeleteClick = (sourceId) => {
    const normalizedId = String(sourceId || '').trim();
    if (!normalizedId) {
      return;
    }
    if (pendingDeleteSourceId === normalizedId) {
      handleRemoveSource(normalizedId);
      setPendingDeleteSourceId('');
      return;
    }
    if (!sourceKeySet.has(normalizedId)) {
      setPendingDeleteSourceId('');
      return;
    }
    setPendingDeleteSourceId(normalizedId);
  };

  const handleDeleteCancel = (sourceId) => {
    const normalizedId = String(sourceId || '').trim();
    if (pendingDeleteSourceId !== normalizedId) {
      return;
    }
    setPendingDeleteSourceId('');
  };

  const handleSourceDetailClick = (source) => {
    if (typeof handleViewSourceDetail === 'function') handleViewSourceDetail(source);
    setSelectedSource(source);
  };

  const handleShellBackgroundClick = () => {
    if (!pendingDeleteSourceId) {
      return;
    }
    setPendingDeleteSourceId('');
  };

  const handleSourceDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const hasFiles = Array.from(Object.assign({}, event.dataTransfer).types ?? []).includes('Files');
    if (!hasFiles) {
      return;
    }
    sourceDragDepthRef.current += 1;
    setSourceDropActive(true);
  };

  const handleSourceDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const hasFiles = Array.from(Object.assign({}, event.dataTransfer).types ?? []).includes('Files');
    if (!hasFiles) {
      return;
    }
    event.dataTransfer.dropEffect = 'copy';
    setSourceDropActive(true);
  };

  const handleSourceDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    sourceDragDepthRef.current = Math.max(0, sourceDragDepthRef.current - 1);
    if (sourceDragDepthRef.current === 0) {
      setSourceDropActive(false);
    }
  };

  const handleSourceDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    sourceDragDepthRef.current = 0;
    setSourceDropActive(false);
    const droppedFiles = Array.from(Object.assign({}, event.dataTransfer).files ?? []);
    if (droppedFiles.length === 0) {
      return;
    }
    if (typeof handleOpenSourcePicker === 'function') handleOpenSourcePicker(droppedFiles);
  };

  return (
    <div
      className={['flare-chat-sources-panel', conditionalValue(isCompactLayout, 'is-compact', '')].filter(Boolean).join(' ')}
      style={{ background: themeTokens.appBg, display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}
    >
      <div
        className="flare-source-collection-scroll"
        style={{
          background: themeTokens.appBg,
          flex: 1,
          overflow: 'auto',
        }}
      >
        <ChatWorkspaceSectionShell
          className={['flare-source-drop-zone', conditionalValue(sourceDropActive, 'is-drag-over', '')].filter(Boolean).join(' ')}
          background={themeTokens.appBg}
          contentMaxWidth={contentMaxWidth}
          onClick={handleShellBackgroundClick}
          onDragEnter={handleSourceDragEnter}
          onDragOver={handleSourceDragOver}
          onDragLeave={handleSourceDragLeave}
          onDrop={handleSourceDrop}
          padding={conditionalValue(isCompactLayout, '8px 12px 4px', '10px 20px 8px')}
          style={{ minHeight: '100%' }}
        >
          <div className="flare-source-drop-zone-inner" style={{ minHeight: '100%' }}>
            <Button
              className="flare-source-add-button"
              disabled={sourceUploadLoading}
              icon={<PlusOutlined />}
              loading={sourceUploadLoading}
              onClick={handleOpenSourcePicker}
              style={{
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 12,
                color: themeTokens.textPrimary,
                display: 'flex',
                gap: 10,
                padding: '11px 14px',
              }}
            >
              <Text style={{ color: themeTokens.textPrimary, fontWeight: 500 }}>
                {conditionalValue(
                  sourceUploadLoading,
                  firstTruthy(resolvedUILabels.sources_uploading, '上传中...'),
                  firstTruthy(resolvedUILabels.sources_add_button, '添加资料')
                )}
              </Text>
            </Button>
            {conditionalValue(Boolean(sourceActionError), (
              <div style={{ paddingTop: 10 }}>
                <Alert
                  title={firstTruthy(resolvedUILabels.sources_error_title, '资料操作失败')}
                  description={firstTruthy(Object.assign({}, sourceActionError).message, String(sourceActionError))}
                  showIcon
                  type="error"
                />
              </div>
            ))}

            {conditionalValue(sourceSyncLoading, (
              <div style={{ alignItems: 'center', display: 'flex', gap: 8, padding: '14px 0 4px' }}>
                <Spin size="small" />
                <Text className="flare-hint-text" style={{ color: themeTokens.textSecondary }} type="secondary">
                  {firstTruthy(resolvedUILabels.sources_loading, '资料加载中...')}
                </Text>
              </div>
            ), conditionalValue(sourceItems.length === 0, (
              <div className="flare-source-list-empty">
                <Text className="flare-hint-text" type="secondary">
                  {firstTruthy(resolvedUILabels.sources_empty_description, '上传到资料区后，后续会话可以复用这些资料。')}
                </Text>
              </div>
            ), (
              <div aria-label="项目资料" className="flare-source-list">
                {sourceItems.map((source, index) => (
                  <SourceMaterialRow
                    key={source.source_id || source.id || `${source.name}-${index}`}
                    source={source}
                    index={index}
                    themeTokens={themeTokens}
                    handleViewSourceDetail={handleSourceDetailClick}
                    handleDeleteCancel={handleDeleteCancel}
                    handleDeleteClick={handleDeleteClick}
                    handleRetrySource={handleRetrySource}
                    pendingDeleteSourceId={pendingDeleteSourceId}
                    sourceRemovingId={sourceRemovingId}
                  />
                ))}
              </div>
            ))) }
          </div>
        </ChatWorkspaceSectionShell>
      </div>
      <SourceDetailModal
        source={selectedSource}
        onClose={() => setSelectedSource(null)}
      />
      {conditionalValue([isCompactLayout, composerNode].every(Boolean), (
        <div className="flare-chat-mobile-composer">
          {composerNode}
        </div>
      ), composerNode)}
    </div>
  );
}
